import { motion } from "framer-motion";
import {
  Pencil,
  Search,
  Image as ImageIcon,
  Sparkles,
  Lightbulb,
  LayoutGrid,
  Star,
  Copy,
  Clapperboard,
  ArrowRight,
} from "lucide-react";
import { cn } from "@/lib/utils";

// Toggle the in-app UI preview row (Hook / Napisy / Ciekawostki carousels + the
// expanded Scenariusz body). Hidden for now per design feedback — flip to true
// when we're ready to bring it back.
const SHOW_UI_PREVIEW = false;

type Sticky = {
  Icon: typeof Pencil;
  iconColor: string;
  bg: string;
  tape: string;
  title: string;
  body: string;
  /** Absolute placement on the desktop board (px) */
  x: number;
  y: number;
  rot: number;
  w: number;
};

const STICKIES: Sticky[] = [
  {
    Icon: Pencil,
    iconColor: "#1A7ACC",
    bg: "#D9ECFB",
    tape: "#1A7ACC",
    title: "Otwarcie filmu",
    body: "Pierwsze 3 sekundy, które zatrzymują scroll. 10 alternatywnych propozycji.",
    x: 30, y: 0, rot: -4, w: 230,
  },
  {
    Icon: Search,
    iconColor: "#B58A0F",
    bg: "#FFF1B8",
    tape: "#E8A30A",
    title: "Fact-checking",
    body: "Zweryfikowane i sprawdzone informacje, które możesz podać dalej.",
    x: 740, y: 20, rot: 3, w: 230,
  },
  {
    Icon: ImageIcon,
    iconColor: "#E8630A",
    bg: "#FFE2C7",
    tape: "#E8630A",
    title: "Napisy na film",
    body: "Nakładki tekstowe, które działają jak haczyk i chwytają widza.",
    x: 0, y: 240, rot: -3, w: 220,
  },
  {
    Icon: Sparkles,
    iconColor: "#7C4FAB",
    bg: "#E5D9F8",
    tape: "#7C4FAB",
    title: "Techniki pisarskie",
    body: "Sprawdzone chwyty, które zwiększają retencję widza.",
    x: 760, y: 260, rot: 4, w: 220,
  },
  {
    Icon: Lightbulb,
    iconColor: "#2D8B4E",
    bg: "#D8EFC9",
    tape: "#2D8B4E",
    title: "Psychologia widza",
    body: "Zrozum, co naprawdę dzieje się w głowie Twoich odbiorców.",
    x: 28, y: 470, rot: 2, w: 230,
  },
  {
    Icon: LayoutGrid,
    iconColor: "#1A7ACC",
    bg: "#D9ECFB",
    tape: "#1A7ACC",
    title: "Format",
    body: "Dostosowujemy format wideo do tematu, by oddać charakter pomysłu.",
    x: 384, y: 510, rot: -2, w: 210,
  },
  {
    Icon: Star,
    iconColor: "#C84B4B",
    bg: "#FDDDE0",
    tape: "#C84B4B",
    title: "Ciekawostki",
    body: "Spraw, że Twoje rolki pozostaną w pamięci na długo.",
    x: 770, y: 510, rot: 3, w: 220,
  },
];

// `fluid` stretches the SVG to its container's width while keeping the
// stroke at a fixed pixel thickness — use it when the parent is
// responsive and we can't predict its rendered width.
const Squiggle = ({
  w = 420,
  color = "#1A7ACC",
  stroke = 5,
  fluid = false,
}: {
  w?: number;
  color?: string;
  stroke?: number;
  fluid?: boolean;
}) => (
  <svg
    width={fluid ? "100%" : w}
    height="14"
    viewBox={`0 0 ${w} 14`}
    preserveAspectRatio={fluid ? "none" : "xMidYMid meet"}
    className="block"
    aria-hidden="true"
  >
    <path
      d={`M2 9 Q ${w * 0.2} 2, ${w * 0.4} 7 T ${w * 0.78} 8 T ${w - 2} 6`}
      stroke={color}
      strokeWidth={stroke}
      strokeLinecap="round"
      fill="none"
      vectorEffect={fluid ? "non-scaling-stroke" : undefined}
    />
  </svg>
);

const StickyNote = ({ n, compact = false }: { n: Sticky; compact?: boolean }) => {
  const Icon = n.Icon;
  return (
    <div
      style={{
        background: n.bg,
        transform: `rotate(${n.rot}deg)`,
        boxShadow:
          "0 12px 22px -10px rgba(26,26,26,0.22), 0 3px 6px -3px rgba(26,26,26,0.12)",
      }}
      className={cn(
        "relative rounded-[3px]",
        compact ? "p-3.5" : "p-5",
      )}
    >
      {/* Tape strip */}
      <div
        style={{
          background: n.tape,
          opacity: 0.55,
          transform: `translateX(-50%) rotate(${-n.rot * 2}deg)`,
          borderLeft: "1px dashed rgba(0,0,0,0.08)",
          borderRight: "1px dashed rgba(0,0,0,0.08)",
        }}
        className={cn(
          "absolute left-1/2 top-[-10px]",
          compact ? "w-12 h-3" : "w-16 h-4",
        )}
      />
      <div className="flex items-center gap-2 mb-2">
        <Icon
          className={compact ? "w-3.5 h-3.5" : "w-4 h-4"}
          color={n.iconColor}
          strokeWidth={2.3}
        />
        <span
          className={cn(
            "font-bold tracking-tight text-[#1A1A1A] leading-none",
            compact ? "text-base" : "text-[19px]",
          )}
        >
          {n.title}
        </span>
      </div>
      <p
        className={cn(
          "text-[#2a2a2a] leading-snug",
          compact ? "text-[12px]" : "text-[13.5px]",
        )}
      >
        {n.body}
      </p>
    </div>
  );
};

const ScenariuszCard = () => (
  <div
    className="relative w-[440px] bg-white rounded-2xl px-7 py-6"
    style={{
      boxShadow:
        "0 30px 60px -30px rgba(26,26,26,0.25), 0 6px 14px -8px rgba(26,26,26,0.12), 0 0 0 1px rgba(26,26,26,0.04)",
    }}
  >
    {/* Tape */}
    <div
      style={{
        background: "rgba(26,122,204,0.4)",
        transform: "translateX(-50%) rotate(-3deg)",
        borderLeft: "1px dashed rgba(0,0,0,0.06)",
        borderRight: "1px dashed rgba(0,0,0,0.06)",
      }}
      className="absolute left-1/2 top-[-10px] w-[90px] h-[18px]"
    />
    <div className="flex items-center gap-3">
      <span className="w-10 h-10 rounded-lg bg-[#1A1A1A] text-white flex items-center justify-center">
        <Clapperboard className="w-5 h-5" />
      </span>
      <span className="font-extrabold text-[26px] tracking-tight">Scenariusz</span>
    </div>
    <p className="mt-3.5 text-[14px] leading-relaxed text-[#3a3a3a]">
      Kompletny skrypt zbudowany na analizie trendów, otwarciach filmu, danych i sprawdzonych technikach pisarskich. Gotowy do publikacji.
    </p>
    <div className="mt-5 grid grid-cols-2 gap-x-5 gap-y-3">
      {[
        "Viralowy potencjał",
        "Struktura oparta na danych",
        "Naturalny flow i narracja",
        "Optymalizacja pod Reels",
      ].map((t) => (
        <div key={t} className="flex items-center gap-2 text-[13.5px] text-[#1A1A1A]">
          <span className="w-5 h-5 rounded-full border-2 border-[#1A7ACC] text-[#1A7ACC] text-[10px] font-extrabold flex items-center justify-center flex-[0_0_20px]">
            ✓
          </span>
          {t}
        </div>
      ))}
    </div>
    <div className="mt-5 px-3.5 py-3 bg-[#F7F4EF] rounded-lg flex items-center gap-2.5">
      <span className="text-[12px] text-gray-500 italic whitespace-nowrap">
        piszę Twój scenariusz
      </span>
      <div className="flex-1 h-1.5 bg-[#E5E2DC] rounded-full overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{
            width: "78%",
            background: "linear-gradient(90deg, #E8630A, #FF8C00)",
          }}
        />
      </div>
      <span className="text-[11px] font-bold text-[#E8630A] font-mono">~38s</span>
    </div>
  </div>
);

const ScenariuszCardCompact = () => (
  <div
    className="relative w-full bg-white rounded-2xl px-5 py-5"
    style={{
      boxShadow:
        "0 20px 40px -20px rgba(26,26,26,0.22), 0 4px 10px -4px rgba(26,26,26,0.1), 0 0 0 1px rgba(26,26,26,0.04)",
    }}
  >
    <div
      style={{
        background: "rgba(26,122,204,0.4)",
        transform: "translateX(-50%) rotate(-3deg)",
        borderLeft: "1px dashed rgba(0,0,0,0.06)",
        borderRight: "1px dashed rgba(0,0,0,0.06)",
      }}
      className="absolute left-1/2 top-[-8px] w-[76px] h-3.5"
    />
    <div className="flex items-center gap-2.5">
      <span className="w-8 h-8 rounded-lg bg-[#1A1A1A] text-white flex items-center justify-center">
        <Clapperboard className="w-4 h-4" />
      </span>
      <span className="font-extrabold text-[22px] tracking-tight">Scenariusz</span>
    </div>
    <p className="mt-3 text-[13px] leading-relaxed text-[#3a3a3a]">
      Kompletny skrypt zbudowany na analizie trendów, otwarciach filmu, danych i sprawdzonych technikach pisarskich.
    </p>
    <div className="mt-3.5 flex flex-col gap-2">
      {[
        "Viralowy potencjał",
        "Struktura oparta na danych",
        "Naturalny flow i narracja",
      ].map((t) => (
        <div key={t} className="flex items-center gap-2 text-[12.5px] text-[#1A1A1A]">
          <span className="w-[18px] h-[18px] rounded-full border-2 border-[#1A7ACC] text-[#1A7ACC] text-[10px] font-extrabold flex items-center justify-center flex-[0_0_18px]">
            ✓
          </span>
          {t}
        </div>
      ))}
    </div>
    <div className="mt-4 px-3 py-2.5 bg-[#F7F4EF] rounded-lg flex items-center gap-2.5">
      <span className="text-[11px] text-gray-500 italic whitespace-nowrap">
        piszę Twój scenariusz
      </span>
      <div className="flex-1 h-1 bg-[#E5E2DC] rounded-full overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{
            width: "78%",
            background: "linear-gradient(90deg, #E8630A, #FF8C00)",
          }}
        />
      </div>
      <span className="text-[10px] font-bold text-[#E8630A] font-mono">~38s</span>
    </div>
  </div>
);

type PreviewCardProps = {
  icon: typeof Pencil;
  iconBg: string;
  iconColor: string;
  title: string;
  body: string;
  count: string;
  badge?: string;
};

const PreviewCard = ({
  icon: Icon,
  iconBg,
  iconColor,
  title,
  body,
  count,
  badge,
}: PreviewCardProps) => (
  <div
    className="bg-white rounded-2xl px-4 py-3.5"
    style={{
      boxShadow:
        "0 12px 24px -16px rgba(26,26,26,0.18), 0 0 0 1px rgba(26,26,26,0.05)",
    }}
  >
    <div className="flex items-center gap-2 mb-2.5">
      <span
        style={{ background: iconBg, color: iconColor }}
        className="w-6 h-6 rounded-md text-xs font-extrabold inline-flex items-center justify-center"
      >
        <Icon className="w-3.5 h-3.5" strokeWidth={2.3} />
      </span>
      <span className="font-bold text-[13px]">{title}</span>
      {badge && (
        <span className="ml-auto bg-[#E6F3EB] text-[#2D8B4E] px-2 py-[3px] rounded-full text-[10px] font-bold tracking-wider">
          {badge}
        </span>
      )}
    </div>
    <div className="bg-[#F7F4EF] px-3 py-2.5 rounded-lg text-[13.5px] leading-snug text-[#1A1A1A] min-h-[72px]">
      {body}
    </div>
    <div className="flex items-center justify-between mt-2.5 text-[11px] text-gray-500 font-mono">
      <span>‹</span>
      <span>{count}</span>
      <span>›</span>
    </div>
  </div>
);

const PreviewScript = () => (
  <div
    className="bg-white rounded-2xl px-6 py-5 h-full"
    style={{
      boxShadow:
        "0 24px 48px -28px rgba(26,26,26,0.22), 0 0 0 1px rgba(26,26,26,0.05)",
    }}
  >
    <div className="flex items-center gap-2.5 mb-3.5">
      <span className="w-7 h-7 rounded-lg bg-[#1A1A1A] text-white inline-flex items-center justify-center">
        <Clapperboard className="w-4 h-4" />
      </span>
      <span className="font-extrabold text-lg">Scenariusz</span>
      <div className="ml-auto flex gap-2">
        <button
          type="button"
          className="border border-[#E5E2DC] bg-white px-3 py-1.5 rounded-md text-[12px] font-semibold inline-flex items-center gap-1.5 hover:bg-gray-50 transition-colors"
        >
          <Copy className="w-3 h-3" />
          Skopiuj
        </button>
        <button
          type="button"
          className="border border-[#E5E2DC] bg-white px-3 py-1.5 rounded-md text-[12px] font-semibold inline-flex items-center gap-1.5 hover:bg-gray-50 transition-colors"
        >
          <Pencil className="w-3 h-3" />
          Edytuj
        </button>
      </div>
    </div>
    <div className="text-[13.5px] leading-[1.6] text-[#1A1A1A] space-y-3">
      <p>
        Dwie osoby robią po milion wyświetleń. Jedna zyskuje garstkę obserwujących, druga buduje potężną społeczność. I nie, to nie kwestia szczęścia.
      </p>
      <p className="text-[#3a3a3a]">
        Problem w tym, że my wszyscy jaramy się nie tym, co trzeba. Liczba wyświetleń wygląda jak sukces. Duża cyfra, dopamina, screen na stories. Tylko że algorytm nie nagradza samego „wybuchu".
      </p>
      <p className="text-gray-500">
        Patrzy, co ludzie robią dalej — czy oglądają do końca, czy wracają drugi raz, czy udostępniają komuś prywatnie.{" "}
        <span className="bg-[#FFEFE2] text-[#E8630A] px-1 py-0.5 rounded font-semibold">
          Powtórne odtworzenia
        </span>{" "}
        i długi czas oglądania to dla platformy sygnał: „to jest coś wartościowego".
      </p>
    </div>
  </div>
);

const PreviewScriptCompact = () => (
  <div
    className="bg-white rounded-2xl px-5 py-4"
    style={{
      boxShadow:
        "0 16px 32px -18px rgba(26,26,26,0.22), 0 0 0 1px rgba(26,26,26,0.05)",
    }}
  >
    <div className="flex items-center gap-2 mb-3">
      <span className="w-7 h-7 rounded-lg bg-[#1A1A1A] text-white inline-flex items-center justify-center">
        <Clapperboard className="w-3.5 h-3.5" />
      </span>
      <span className="font-extrabold text-base">Scenariusz</span>
      <div className="ml-auto flex gap-1.5">
        <button
          type="button"
          aria-label="Skopiuj"
          className="border border-[#E5E2DC] bg-white p-1.5 rounded-md hover:bg-gray-50 transition-colors"
        >
          <Copy className="w-3 h-3" />
        </button>
        <button
          type="button"
          aria-label="Edytuj"
          className="border border-[#E5E2DC] bg-white p-1.5 rounded-md hover:bg-gray-50 transition-colors"
        >
          <Pencil className="w-3 h-3" />
        </button>
      </div>
    </div>
    <div className="text-[13px] leading-[1.6] text-[#1A1A1A] space-y-2.5">
      <p>
        Dwie osoby robią po milion wyświetleń. Jedna zyskuje garstkę obserwujących, druga buduje społeczność. I nie, to nie kwestia szczęścia.
      </p>
      <p className="text-gray-500">
        Algorytm patrzy, co ludzie robią dalej — czy oglądają do końca, czy wracają drugi raz.{" "}
        <span className="bg-[#FFEFE2] text-[#E8630A] px-1 py-0.5 rounded font-semibold">
          Powtórne odtworzenia
        </span>{" "}
        to dla platformy sygnał wartości.
      </p>
    </div>
  </div>
);

const CreativeBoardSection = () => (
  <section
    id="warsztat"
    className="relative py-20 md:py-28 overflow-hidden scroll-mt-24 bg-[#F7F4EF] text-[#1A1A1A]"
  >
    {/* Top bridge — fades in from dark VideoSection above */}
    <div
      aria-hidden="true"
      className="absolute inset-x-0 top-0 h-32 md:h-40 pointer-events-none"
      style={{ background: "linear-gradient(to bottom, #1a1a2e, transparent)" }}
    />
    {/* Bottom bridge — fades out to peach ClientPanelSection below */}
    <div
      aria-hidden="true"
      className="absolute inset-x-0 bottom-0 h-32 md:h-40 pointer-events-none"
      style={{ background: "linear-gradient(to top, #FCCAB0, transparent)" }}
    />

    <div className="relative z-10 max-w-[1480px] mx-auto px-4 sm:px-8 lg:px-16">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="text-center max-w-[920px] mx-auto"
      >
        <h2 className="font-extrabold leading-[1.04] tracking-tight text-4xl md:text-6xl lg:text-[72px]">
          Wszystko do{" "}
          <span className="text-[#E8630A]">viralowych treści.</span>
          <br />
          <span className="relative inline-block">
            <span className="text-[#1A7ACC]">W</span> jednym miejscu.
            <span
              aria-hidden="true"
              className="absolute inset-x-2 -bottom-1.5 md:inset-x-6 md:-bottom-2 pointer-events-none"
            >
              <Squiggle color="#1A7ACC" stroke={4} fluid />
            </span>
          </span>
        </h2>
        <p className="mt-7 max-w-[620px] mx-auto text-base md:text-lg leading-relaxed text-[#3a3a3a]">
          Kompletny warsztat, który zamienia pomysł w treść, która zatrzymuje uwagę i buduje zaangażowanie.
        </p>
      </motion.div>

      {/* ─── DESKTOP BOARD ─── */}
      <div className="hidden lg:block relative mt-20 h-[760px] mx-auto" style={{ width: 1000 }}>
        {STICKIES.map((n, i) => (
          <div
            key={i}
            className="absolute"
            style={{ left: n.x, top: n.y, width: n.w }}
          >
            <StickyNote n={n} />
          </div>
        ))}
        {/* Central scenariusz card */}
        <div className="absolute left-1/2 top-32 -translate-x-1/2">
          <ScenariuszCard />
        </div>
        {/* Dashed arrows pointing to the central card */}
        <svg
          aria-hidden="true"
          className="absolute inset-0 w-full h-full pointer-events-none overflow-visible"
        >
          <defs>
            <marker
              id="arr-cb-board"
              viewBox="0 0 10 10"
              refX="9"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto"
            >
              <path d="M0,0 L10,5 L0,10 z" fill="#9a9a9a" />
            </marker>
          </defs>
          {/* TL sticky → top edge of card */}
          <path d="M 255 100 Q 360 105, 440 128" stroke="#9a9a9a" strokeWidth="1.5" fill="none" strokeDasharray="4 5" markerEnd="url(#arr-cb-board)" />
          {/* TR sticky → top edge */}
          <path d="M 745 115 Q 660 110, 580 128" stroke="#9a9a9a" strokeWidth="1.5" fill="none" strokeDasharray="4 5" markerEnd="url(#arr-cb-board)" />
          {/* ML sticky → left edge */}
          <path d="M 220 320 Q 255 305, 280 275" stroke="#9a9a9a" strokeWidth="1.5" fill="none" strokeDasharray="4 5" markerEnd="url(#arr-cb-board)" />
          {/* MR sticky → right edge */}
          <path d="M 760 340 Q 740 320, 720 290" stroke="#9a9a9a" strokeWidth="1.5" fill="none" strokeDasharray="4 5" markerEnd="url(#arr-cb-board)" />
          {/* BL sticky → bottom edge */}
          <path d="M 280 540 Q 350 490, 400 418" stroke="#9a9a9a" strokeWidth="1.5" fill="none" strokeDasharray="4 5" markerEnd="url(#arr-cb-board)" />
          {/* BC sticky → bottom edge */}
          <path d="M 490 510 Q 495 460, 500 418" stroke="#9a9a9a" strokeWidth="1.5" fill="none" strokeDasharray="4 5" markerEnd="url(#arr-cb-board)" />
          {/* BR sticky → bottom edge */}
          <path d="M 770 540 Q 680 490, 600 418" stroke="#9a9a9a" strokeWidth="1.5" fill="none" strokeDasharray="4 5" markerEnd="url(#arr-cb-board)" />
        </svg>
      </div>

      {/* ─── DESKTOP UI PREVIEW ROW ─── */}
      {SHOW_UI_PREVIEW && (
        <div className="hidden lg:grid mt-8 gap-8" style={{ gridTemplateColumns: "340px 1fr" }}>
          <div className="flex flex-col gap-4">
            <PreviewCard
              icon={Pencil}
              iconBg="#FFEFE2"
              iconColor="#E8630A"
              title="Otwarcie filmu"
              body="Milion wyświetleń i dalej cisza w obserwacjach?"
              count="1 / 10"
            />
            <PreviewCard
              icon={ImageIcon}
              iconBg="#FFEFE2"
              iconColor="#E8630A"
              title="Napisy tekstowe"
              body={'„Wiral bez obserwujących to pusty hałas."'}
              count="1 / 10"
            />
            <PreviewCard
              icon={Star}
              iconBg="#E6F3EB"
              iconColor="#2D8B4E"
              title="Ciekawostki"
              body="Szybki wzrost liczby obserwujących często powoduje krótki spadek zasięgu…"
              count="1 / 5"
              badge="FAKT"
            />
          </div>
          <PreviewScript />
        </div>
      )}

      {/* ─── MOBILE / TABLET LAYOUT ─── */}
      <div className="lg:hidden mt-12">
        {/* Top stickies — flank the card */}
        <div className="grid grid-cols-2 gap-3">
          <StickyNote n={STICKIES[0]} compact />
          <StickyNote n={STICKIES[1]} compact />
        </div>

        {/* Central Scenariusz */}
        <div className="mt-7">
          <ScenariuszCardCompact />
        </div>

        {/* Middle + bottom stickies */}
        <div className="mt-8 grid grid-cols-2 gap-x-3 gap-y-6">
          <StickyNote n={STICKIES[2]} compact />
          <StickyNote n={STICKIES[3]} compact />
          <StickyNote n={STICKIES[4]} compact />
          <StickyNote n={STICKIES[5]} compact />
        </div>

        {/* Lone bottom sticky */}
        <div className="mt-8 flex justify-center">
          <div className="w-[80%] max-w-[260px]">
            <StickyNote n={STICKIES[6]} compact />
          </div>
        </div>

        {SHOW_UI_PREVIEW && (
          <>
            {/* Section divider */}
            <div className="mt-14 mb-5 text-center text-[#6B6B6B] italic text-base md:text-lg">
              ↓ w aplikacji wygląda to tak ↓
            </div>

            {/* UI preview stack */}
            <div className="flex flex-col gap-3">
              <PreviewScriptCompact />
              <PreviewCard
                icon={Pencil}
                iconBg="#FFEFE2"
                iconColor="#E8630A"
                title="Otwarcie filmu"
                body="Milion wyświetleń i dalej cisza w obserwacjach?"
                count="1 / 10"
              />
              <PreviewCard
                icon={ImageIcon}
                iconBg="#FFEFE2"
                iconColor="#E8630A"
                title="Napisy tekstowe"
                body={'„Wiral bez obserwujących to pusty hałas."'}
                count="1 / 10"
              />
              <PreviewCard
                icon={Star}
                iconBg="#E6F3EB"
                iconColor="#2D8B4E"
                title="Ciekawostki"
                body="Szybki wzrost liczby obserwujących często powoduje krótki spadek zasięgu…"
                count="1 / 5"
                badge="FAKT"
              />
            </div>
          </>
        )}
      </div>

      {/* Closer tagline */}
      <div className="mt-16 md:mt-20 text-center">
        <div className="inline-block relative italic font-extrabold text-2xl md:text-3xl lg:text-4xl text-[#1A1A1A] leading-tight">
          Od pomysłu do treści, która działa.
          <span
            aria-hidden="true"
            className="absolute inset-x-0 -bottom-1.5 sm:-bottom-2 pointer-events-none"
          >
            <Squiggle color="#E8630A" stroke={3} fluid />
          </span>
        </div>
        <div className="mt-7 flex items-center justify-center gap-3 text-sm md:text-base font-semibold text-gray-500">
          <span>Szybciej.</span>
          <span className="text-[#1A1A1A]">·</span>
          <span>Mądrzej.</span>
          <span className="text-[#1A1A1A]">·</span>
          <span className="text-[#E8630A]">Skuteczniej.</span>
        </div>
      </div>

      {/* CTA — orange pill, matches the rest of the landing */}
      <div className="mt-10 md:mt-12 flex justify-center">
        <a
          href="/auth?mode=register&trial=true"
          className="relative overflow-hidden group inline-flex items-center gap-2 px-8 py-4 bg-orange-500 text-white font-bold text-lg rounded-full shadow-xl shadow-orange-500/25 hover:bg-orange-600 hover:shadow-2xl active:scale-[0.98] transition-all duration-300"
        >
          <span
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(120deg, transparent 35%, rgba(255,255,255,0.15) 50%, transparent 65%)",
              backgroundSize: "200% 100%",
              animation: "shimmer-sweep 4s ease-in-out infinite",
            }}
          />
          <span className="relative z-10">Wypróbuj 5 dni za darmo</span>
          <ArrowRight className="relative z-10 w-5 h-5 transition-transform group-hover:translate-x-1" />
        </a>
      </div>
    </div>
  </section>
);

export default CreativeBoardSection;
